/**
 * selection.js
 *
 * Exercise similarity scoring and swap ranking for Gymbo.
 * Covers PR-01 (no near-duplicate swaps), PR-02 (no near-duplicates within a
 * session), PR-03 (exclusions), PR-04 (swap always returns something).
 *
 * Pure functions only. No I/O, no network, no clock, no randomness.
 * Same inputs always produce the same output, in the same order.
 *
 * Expects Exercise objects as defined in docs/product-spec.md.
 */

// ---------------------------------------------------------------------------
// Tunable constants
//
// UNSOURCED. These are starting guesses, in the same category as
// MINUTES_PER_SET. They need tuning against real swaps before they are
// treated as settled.
// ---------------------------------------------------------------------------

/** Above this, two compound exercises are considered the same movement. */
export const NEAR_DUPLICATE_CEILING_COMPOUND = 0.82;

/** Above this, two isolation exercises are considered the same movement.
 * Higher than compound because isolation exercises have narrower movement space.
 * Most bicep exercises are curl variants scoring above 0.8, so 0.82 would
 * reject the entire legitimate pool. */
export const NEAR_DUPLICATE_CEILING_ISOLATION = 0.92;

/** Swap aims for this score: same job, different stimulus. */
export const SWAP_TARGET_SIMILARITY = 0.6;

/** Below this, a swap is not a meaningful replacement at all. */
export const SWAP_FLOOR_SIMILARITY = 0.15;

/** Get the near-duplicate ceiling for a given mechanic. */
export function getNearDuplicateCeiling(mechanic) {
  return mechanic === 'isolation'
    ? NEAR_DUPLICATE_CEILING_ISOLATION
    : NEAR_DUPLICATE_CEILING_COMPOUND;
}

export const WEIGHTS = {
  movementPattern: 0.3,
  primaryMuscles: 0.3,
  equipment: 0.2,
  nameTokens: 0.2,
};

// ---------------------------------------------------------------------------
// Name normalisation
//
// Qualifiers describe a variant of the same movement, so they are stripped
// before comparing names. Equipment words are stripped too, because equipment
// is already scored on its own axis and would otherwise be counted twice.
// ---------------------------------------------------------------------------

const QUALIFIERS = new Set([
  'wide', 'close', 'narrow', 'medium', 'neutral', 'grip', 'stance',
  'reverse', 'inverted', 'alternate', 'alternating', 'single', 'one', 'two',
  'seated', 'standing', 'kneeling', 'lying', 'prone', 'supine', 'bent',
  'incline', 'decline', 'flat', 'high', 'low', 'front', 'rear', 'side',
  'over', 'under', 'behind', 'across', 'to', 'the', 'and', 'with', 'on',
  'arm', 'arms', 'leg', 'legs', 'handed', 'hand',
  'assisted', 'weighted', 'bodyweight', 'body', 'only',
  'partial', 'full', 'half', 'deficit', 'paused', 'tempo', 'explosive',
]);

const EQUIPMENT_WORDS = new Set([
  'barbell', 'dumbbell', 'dumbbells', 'cable', 'cables', 'machine',
  'smith', 'kettlebell', 'band', 'bands', 'ez', 'bar', 'plate',
  'bench', 'rack', 'ball', 'rope', 'trx', 'sled', 'medicine',
]);

/** Split a name into the tokens that identify the movement itself. */
export function movementTokens(name) {
  if (!name) return [];
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s-]+/)
    .filter((t) => t.length > 1)
    .filter((t) => !QUALIFIERS.has(t))
    .filter((t) => !EQUIPMENT_WORDS.has(t));
}

// ---------------------------------------------------------------------------
// Set comparison
// ---------------------------------------------------------------------------

/** Jaccard index of two string lists. Empty on both sides scores 1. */
export function jaccard(a = [], b = []) {
  const setA = new Set((a || []).map((s) => String(s).toLowerCase()));
  const setB = new Set((b || []).map((s) => String(s).toLowerCase()));
  if (setA.size === 0 && setB.size === 0) return 1;
  if (setA.size === 0 || setB.size === 0) return 0;
  let shared = 0;
  for (const v of setA) if (setB.has(v)) shared += 1;
  return shared / (setA.size + setB.size - shared);
}

// ---------------------------------------------------------------------------
// Similarity
// ---------------------------------------------------------------------------

/**
 * Score how alike two exercises are as movements. Returns 0 to 1.
 * 1.0 means the same movement. Comparing an exercise to itself returns 1.
 */
export function similarity(a, b) {
  if (!a || !b) return 0;
  if (a.id && a.id === b.id) return 1;

  const patternScore =
    a.movementPattern && a.movementPattern === b.movementPattern ? 1 : 0;

  return (
    WEIGHTS.movementPattern * patternScore +
    WEIGHTS.primaryMuscles * jaccard(a.primaryMuscles, b.primaryMuscles) +
    WEIGHTS.equipment * jaccard(a.equipment, b.equipment) +
    WEIGHTS.nameTokens *
      jaccard(movementTokens(a.name), movementTokens(b.name))
  );
}

/** True when two exercises are close enough to be redundant on the same day.
 * Uses mechanic-specific ceiling: isolation exercises get a higher threshold
 * because they have narrower movement space. */
export function isNearDuplicate(a, b, ceiling = null) {
  if (ceiling === null) {
    // Use the higher of the two mechanics' ceilings to be conservative
    const ceilingA = getNearDuplicateCeiling(a.mechanic);
    const ceilingB = getNearDuplicateCeiling(b.mechanic);
    ceiling = Math.max(ceilingA, ceilingB);
  }
  return similarity(a, b) >= ceiling;
}

// ---------------------------------------------------------------------------
// PR-02: within-session diversity
// ---------------------------------------------------------------------------

/**
 * Drop candidates that duplicate anything already placed in the session.
 *
 * If filtering would empty the pool, the ceiling is relaxed in fixed steps
 * so a slot always fills. The tier-0 and tier-1 pools are small, so failing
 * closed here would produce short sessions.
 *
 * Returns { pool, ceilingUsed, relaxed }.
 */
export function filterNearDuplicates(
  candidates,
  placed,
  ceiling = null
) {
  // If no ceiling specified, use per-mechanic default
  // For relaxation steps, we'll compute them based on the candidate's mechanic
  const steps = ceiling !== null ? [ceiling, 0.88, 0.94, 1.01] : null;

  for (let i = 0; i < (steps ? steps.length : 4); i += 1) {
    const pool = candidates.filter((cand) => {
      return !placed.some((p) => {
        const c = steps ? steps[i] : (
          i === 0 ? null : // use mechanic-specific default
          i === 1 ? 0.88 :
          i === 2 ? 0.94 :
          1.01
        );
        return isNearDuplicate(cand, p, c);
      });
    });
    if (pool.length > 0) {
      const usedCeiling = steps ? steps[i] : 'mechanic-specific';
      return { pool, ceilingUsed: usedCeiling, relaxed: i > 0 };
    }
  }
  return { pool: [], ceilingUsed: 1.01, relaxed: true };
}

// ---------------------------------------------------------------------------
// PR-04: match quality bands
// ---------------------------------------------------------------------------

export const MATCH_QUALITY = {
  EQUIVALENT: 'equivalent',   // same pattern, same primary muscles
  SIMILAR: 'similar',         // same primary muscles, different pattern
  RELATED: 'related',         // overlapping secondary muscles, same mechanic
  SAME_FOCUS: 'same-focus',   // last resort: fits the day, little else
};

export const MATCH_QUALITY_LABEL = {
  equivalent: 'Equivalent swap',
  similar: 'Similar movement',
  related: 'Hits the same area differently',
  'same-focus': 'Different movement, fits today',
};

function overlaps(a = [], b = []) {
  const setB = new Set((b || []).map((s) => String(s).toLowerCase()));
  return (a || []).some((v) => setB.has(String(v).toLowerCase()));
}

function classifyMatch(original, candidate) {
  const samePrimary = overlaps(original.primaryMuscles, candidate.primaryMuscles);
  const samePattern = original.movementPattern === candidate.movementPattern;

  if (samePrimary && samePattern) return MATCH_QUALITY.EQUIVALENT;
  if (samePrimary) return MATCH_QUALITY.SIMILAR;

  const originalAll = [
    ...(original.primaryMuscles || []),
    ...(original.secondaryMuscles || []),
  ];
  const candidateAll = [
    ...(candidate.primaryMuscles || []),
    ...(candidate.secondaryMuscles || []),
  ];
  if (original.mechanic === candidate.mechanic && overlaps(originalAll, candidateAll)) {
    return MATCH_QUALITY.RELATED;
  }
  return MATCH_QUALITY.SAME_FOCUS;
}

const QUALITY_RANK = {
  equivalent: 0,
  similar: 1,
  related: 2,
  'same-focus': 3,
};

// ---------------------------------------------------------------------------
// PR-01 + PR-03 + PR-04: swap ranking
// ---------------------------------------------------------------------------

/**
 * Rank swap candidates for one exercise.
 *
 * @param original            the exercise being replaced
 * @param library             full exercise list
 * @param opts.tier           current equipment tier; candidates must be <= this
 * @param opts.placed         other exercises already in today's session
 * @param opts.excludedIds    "never again" list from user prefs (PR-03)
 * @param opts.skippedTodayIds "not today" list, session scoped (PR-03)
 * @param opts.limit          max results, default 5
 *
 * Always returns at least one option when any exercise is available at the
 * tier and not excluded. Never returns an empty list for a non-empty library.
 */
export function rankSwapCandidates(original, library, opts = {}) {
  const {
    tier = 3,
    placed = [],
    excludedIds = [],
    skippedTodayIds = [],
    limit = 5,
  } = opts;

  const blocked = new Set([
    ...excludedIds,
    ...skippedTodayIds,
    original?.id,
    ...placed.map((p) => p.id),
  ]);

  // Substitutes are working sets, not stretches. Carried over from the
  // existing generator rule (build-log, bug 2).
  const eligible = library.filter(
    (e) =>
      !blocked.has(e.id) &&
      e.minTier <= tier &&
      (e.category === undefined ||
        ['strength', 'powerlifting', 'olympic weightlifting'].includes(e.category))
  );

  const scored = eligible.map((e) => ({
    exercise: e,
    score: similarity(original, e),
    quality: classifyMatch(original, e),
  }));

  // PR-01: near-duplicates are demoted, not offered first.
  // Use mechanic-specific ceiling for the original exercise
  const ceiling = getNearDuplicateCeiling(original.mechanic);
  const usable = scored.filter((s) => s.score < ceiling);
  const duplicates = scored.filter((s) => s.score >= ceiling);

  // Rank by match quality, then by distance from the target band, then by
  // stapleScore, then by id. The last key makes ordering fully deterministic.
  const rank = (list) =>
    [...list].sort((a, b) => {
      const q = QUALITY_RANK[a.quality] - QUALITY_RANK[b.quality];
      if (q !== 0) return q;
      const da = Math.abs(a.score - SWAP_TARGET_SIMILARITY);
      const db = Math.abs(b.score - SWAP_TARGET_SIMILARITY);
      if (da !== db) return da - db;
      const sa = a.exercise.stapleScore ?? 9;
      const sb = b.exercise.stapleScore ?? 9;
      if (sa !== sb) return sa - sb;
      return String(a.exercise.id).localeCompare(String(b.exercise.id));
    });

  const meaningful = rank(usable.filter((s) => s.score >= SWAP_FLOOR_SIMILARITY));
  const weak = rank(usable.filter((s) => s.score < SWAP_FLOOR_SIMILARITY));

  // PR-04: fall through rather than return nothing. Near-duplicates come last
  // because a redundant swap still beats no swap at all.
  const ordered = [...meaningful, ...weak, ...rank(duplicates)];

  return ordered.slice(0, limit).map((s) => ({
    exercise: s.exercise,
    score: Number(s.score.toFixed(4)),
    quality: s.quality,
    label: MATCH_QUALITY_LABEL[s.quality],
    isNearDuplicate: s.score >= ceiling,
  }));
}
